import { registerTheme } from 'echarts'
import type { ManifestWebDomain } from '~/types/Theme'
import { DEFAULT_MANIFEST } from '~/types/Theme'
import { isKey } from '~/types/Utils'

export const useThemeStore = defineStore('Theme', () => {
  type ThemeStyle = 'dark' | 'light'
  const domain = window.location.host.split('.')[0]
  const title = domain.charAt(0).toUpperCase() + domain.slice(1)
  const promise = ref<Promise<ManifestWebDomain>>()
  const { state, execute } = useAsyncState(findTheme().then(r => r.data), DEFAULT_MANIFEST, { immediate: false })
  promise.value = execute()
  const theme = useTheme()
  const loginLogo = computed(() => state.value.loginLogo)
  const sidebarLogo = computed(() => state.value.sidebarLogo)
  const useDarkTheme = computed(() => theme.global.current.value.dark)

  promise.value.then(() => {
    Object.keys(state.value.theme.dark).forEach((key) => {
      if (isKey(state.value.theme.dark, key)) {
        theme.themes.value.dark.colors[key] = state.value.theme.dark[key]
      }
    })
    Object.keys(state.value.theme.light).forEach((key) => {
      if (isKey(state.value.theme.light, key)) {
        theme.themes.value.dark.colors[key] = state.value.theme.light[key]
      }
    })
    theme.themes.value.light.colors.background = '#f8f9fa'
    theme.themes.value.dark.colors.background = '#121212'
    if (localStorage.getItem('theme') === 'dark') {
      updateLocalTheme('dark')
    } else {
      updateLocalTheme('light')
    }

    registerTheme('light', whiteTheme(theme.themes.value.light.colors.primary))
    registerTheme('dark', darkTheme(theme.themes.value.dark.colors.primary))
    useHead({
      title: title,
      meta: [
        { name: 'theme-color', content: state.value.manifest.theme_color },
        { name: 'apple-mobile-web-app-title', content: title },
        { name: 'apple-mobile-web-app-status-bar-style', content: 'black' },
        { name: 'msapplication-TileColor', content: state.value.manifest.theme_color },
        { name: 'viewport', content: 'width=device-width, height=device-height, initial-scale=1.0, minimum-scale=1.0' },
      ],
      link: [
        { rel: 'apple-touch-icon', href: '/api/themes/manifest/favicon/_152x152.png' },
        { rel: 'mask-icon', color: state.value.manifest.theme_color, href: '/api/themes/manifest/favicon/_144x144.png' },
        { rel: 'icon', href: '/api/themes/manifest/favicon/_32x32.png', sizes: '32x32' },
        { rel: 'icon', href: '/api/themes/manifest/favicon/_16x16.png', sizes: '16x16' },
      ],
    })
  })

  function updateLocalTheme(themeStyle: ThemeStyle) {
    theme.global.name.value = themeStyle
    localStorage.setItem('theme', themeStyle)
  }

  return {
    useDarkTheme,
    loginLogo,
    sidebarLogo,
    updateLocalTheme,
  }
})
